import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Price summary of the PDP configurators (snippets/configurator-summary.liquid).
 *
 * The configurator scripts own the price maths and only write the unit price and unit
 * compare-at price into `data-unit-price` / `data-unit-compare`; the summary multiplies
 * them by the form's quantity. The hand-over is an attribute rather than a method call
 * because assets/product-configurator.js is a classic script and can run before this
 * module has defined the element — the upgrade then renders the values already set.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} subtotal
 * @property {HTMLElement} discountRow
 * @property {HTMLElement} discount
 * @property {HTMLElement} total
 * @property {HTMLElement} savingsRow
 * @property {HTMLElement} savings
 *
 * @extends {Component<Refs>}
 */
class ConfiguratorSummaryComponent extends Component {
  static observedAttributes = ['data-unit-price', 'data-unit-compare'];

  requiredRefs = ['subtotal', 'discountRow', 'discount', 'total', 'savingsRow', 'savings'];

  /** quantity-selector dispatches its update event after its +/- buttons run. */
  #quantityEvents = ['change', 'input', ThemeEvents.quantitySelectorUpdate];

  /** @type {HTMLInputElement | null} */
  #quantityInput = null;

  connectedCallback() {
    super.connectedCallback();

    this.#quantityInput = this.closest('form')?.querySelector('input[name="quantity"]') ?? null;
    for (const type of this.#quantityEvents) this.#quantityInput?.addEventListener(type, this.#render);

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const type of this.#quantityEvents) this.#quantityInput?.removeEventListener(type, this.#render);
    this.#quantityInput = null;
  }

  attributeChangedCallback() {
    // Also fires on upgrade, before connectedCallback has collected the refs.
    if (this.refs.total) this.#render();
  }

  #render = () => {
    const { unitPrice, unitCompare, savingsTemplate } = this.dataset;
    if (unitPrice === undefined || unitCompare === undefined) return;

    const quantity = parseInt(this.#quantityInput?.value ?? '', 10) || 1;
    const subtotal = Number(unitCompare) * quantity;
    const total = Number(unitPrice) * quantity;
    const discount = subtotal - total;
    const { refs } = this;

    refs.subtotal.textContent = this.#formatMoney(subtotal);
    refs.total.textContent = this.#formatMoney(total);
    refs.discountRow.hidden = discount <= 0;
    refs.savingsRow.hidden = discount <= 0;
    if (discount <= 0) return;

    const amount = this.#formatMoney(discount);
    refs.discount.textContent = `- ${amount}`;
    refs.savings.textContent = savingsTemplate ? savingsTemplate.replace('{amount}', amount) : amount;
  };

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const number = (cents / 100).toFixed(2).replace('.', this.dataset.decimalSeparator ?? ',');

    return `${number} ${this.dataset.currencySymbol ?? ''}`.trim();
  }
}

if (!customElements.get('configurator-summary-component')) {
  customElements.define('configurator-summary-component', ConfiguratorSummaryComponent);
}
